import React from 'react';

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const Shield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M12 3l7 3v6.5c0 3.5-2.5 6.7-7 8.5-4.5-1.8-7-5-7-8.5V6z" />
  </svg>
);

type Status = 'ok' | 'warn' | 'info';

interface TerminalRow {
  label: string;
  status: Status;
  text: string;
}

const TERMINAL_ROWS: TerminalRow[] = [
  { label: '● Detecting USB key...', status: 'ok', text: 'FOUND' },
  { label: '● Verifying key checksum...', status: 'ok', text: 'OK' },
  { label: '● ZFS: unlocking tank/data', status: 'ok', text: 'OPEN' },
  { label: '● LUKS: opening sda2_crypt', status: 'warn', text: 'SCAFFOLDED' },
  { label: '● Notifying systemd...', status: 'ok', text: 'OK' },
];

/* Hero copy: keep the voice plainspoken and operator-first; highlight problem, ambition, and how to engage. */
const HeroSection: React.FC = () => {
  return (
    <div className="lc-hero-inner">
      <div className="lc-hero-copy">
        <div className="lc-hero-kicker">Rust · LUKS · ZFS · Early boot</div>
        <h1 className="lc-hero-title">
          One key management layer for LUKS and ZFS that operators can read.
        </h1>
        <p className="lc-hero-tagline">
          LockChain pairs modern Rust tooling with a transparent policy engine so you can unlock
          encrypted drives at early boot — LUKS volumes and ZFS datasets, USB key workflows,
          systemd integration — without losing observability or control.
        </p>

        <div className="lc-hero-actions">
          <a
            href="https://github.com/x4ngus/lockchain"
            className="lc-btn lc-btn--primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ArrowRight /> View on GitHub
          </a>
          <a href="#overview" className="lc-btn lc-btn--ghost">
            <Shield /> Explore the idea
          </a>
        </div>

        <div className="lc-hero-meta">
          <span className="lc-badge">Rust 2021</span>
          <span className="lc-badge">LUKS + ZFS</span>
          <span className="lc-badge">CLI · Daemon · USB · TUI</span>
          <span className="lc-badge">v0.2.1-alpha</span>
        </div>
      </div>

      <div className="lc-hero-visual">
        <div className="lc-terminal">
          <div className="lc-terminal-header">
            <span className="lc-terminal-dot lc-terminal-dot--red" />
            <span className="lc-terminal-dot lc-terminal-dot--yellow" />
            <span className="lc-terminal-dot lc-terminal-dot--green" />
            <span className="lc-terminal-title">lockchain unlock — early-boot</span>
          </div>
          <div className="lc-terminal-body">
            <div className="lc-terminal-line">
              <span className="lc-terminal-prompt">$</span>
              <span className="lc-terminal-cmd">lockchain unlock --policy usb-key-primary</span>
            </div>
            <div className="lc-terminal-divider" />
            {TERMINAL_ROWS.map((row) => (
              <div key={row.label} className="lc-terminal-out">
                <span className="lc-terminal-label">{row.label}</span>
                <span className={`lc-terminal-status lc-terminal-status--${row.status}`}>
                  [{row.text}]
                </span>
              </div>
            ))}
            <div className="lc-terminal-divider" />
            <div className="lc-terminal-footer">
              <span>policy: <span className="lc-terminal-footer-tag">usb-key-primary</span></span>
              <span className="lc-terminal-footer-tag">v0.2.1-alpha</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
